import { useMemo, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";

import BasicCard from "@components/cards/BasicCard";
import styles from "@components/cards/Card.module.css";
import GoalPopover from "@components/carePlan/GoalPopover";
import Icon from "@components/text/Icon";
import StatusText from "@components/text/StatusText";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import { JourneyStatusEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyWithPlanQuery["journey"];
type Goal = Journey["goals"][0];

type Props = {
  goal: Goal;
  journey: Journey;
};

const GoalCard = ({ goal, journey }: Props) => {
  const myClient = useMyClient();
  const [isExploding, setIsExploding] = useState(false);

  const status = useMemo(() => {
    if (goal.completed) {
      return JourneyStatusEnum.COMPLETED;
    }
    if (!goal.ownerViewedAt) {
      return JourneyStatusEnum.NEW;
    }
  }, [goal.completed, goal.ownerViewedAt]);

  return (
    <InternalLink
      expand
      stylized={false}
      to={journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: `/care-plan/goals/${goal.id}`,
        myClient
      })}
    >
      <BasicCard hover="purple" inactive={goal.archived}>
        <div className={styles.goalCard} data-test="goal-card">
          <div className={styles.goalCardHeader}>
            <div className={styles.goalCardTags}>
              <div className={styles.goalStatuses}>
                {status && <StatusText status={status} />}
                {goal.archived && <StatusText status={JourneyStatusEnum.ARCHIVED} />}
              </div>
              <GoalPopover
                disabled={goal.hasDuplicateGoalInEditMode}
                goal={goal}
                journey={journey}
                setIsExploding={setIsExploding}
              />
            </div>
          </div>
          <div className="align-center">
            <div className={styles.center}>
              {isExploding && (
                <ConfettiExplosion
                  colors={["#5f49d3", "#82d4e8", "#ff7673", "#FAC7F3", "#F7DBF4"]}
                  duration={2500}
                  force={0.6}
                  particleCount={85}
                  width={1000}
                />
              )}
            </div>

            <Heading2 color={goal.archived ? "gray-300" : "primary"}>{goal.title}</Heading2>
          </div>
          <div className="align-right">
            <Icon color={goal.archived ? "gray-300" : "primary"} icon="arrowUpRightSquare" size="large" />
          </div>
        </div>
      </BasicCard>
    </InternalLink>
  );
};

export default GoalCard;
